import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Router from "..";

// 에이전시, 크리에이터 공통
// CampaignSearch
export const useCampaignList = ({ page, status }) => {
  return useQuery({
    queryKey: ["campaign", "list", page, status],
    queryFn: () => Router.Api.getAllCampaigns({ page, status }),
    staleTime: 1000 * 60,
  });
};

export const useApplyCampaignByAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.applyCampaignByAgency(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list"],
      });
    },
  });
};

export const useApplyCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.applyCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list"],
      });
    },
  });
};

// 크리에이터 지원 내역 상세 조회 (CampaignSearch·MyCampaign 공통)
export const useAppliedCampaignsByInfluencer = ({
  campaignId,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["campaign", "applied", campaignId],
    queryFn: () =>
      Router.Api.getAppliedCampaignsByInfluencer({
        campaign_id: campaignId,
      }),
    enabled: !!campaignId && enabled,
  });
};

// BrandCampaignManagement
// 브랜드 캠페인 관리의 캠페인 목록
export const useCampaignListByBrand = ({ page, status, enabled = true }) => {
  return useQuery({
    queryKey: ["campaign", "list", "brand", page, status],
    queryFn: () => Router.Api.getCampaignsByBrand({ page, status }),
    staleTime: 1000 * 60,
    enabled,
  });
};
// 초대자 목록 조회
export const useInvitedInfluencersByBrand = ({
  campaignId,
  status,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["campaign", "invited", "brand", campaignId, status],
    queryFn: () =>
      Router.Api.getInvitedInfluencersByBrand({ campaignId, status }),
    enabled,
  });
};

// 신청자 목록 조회
export const useAppliedInfluencersByBrand = ({
  campaignId,
  status,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["campaign", "applied", "brand", campaignId, status],
    queryFn: () =>
      Router.Api.getAppliedInfluencersByBrand({ campaignId, status }),
    enabled,
  });
};

export const useManageCampaignRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.manageCampaignRequest(variables),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "brand", variables.campaign_id],
      });
    },
  });
};

export const useSendFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.sendFeedback(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "brand"],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied"],
      });
    },
  });
};

export const useUpdateCampaignStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.updateCampaignStatus(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "brand"],
      });
    },
  });
};

// 캠페인 수정
export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.updateCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "brand"],
      });
    },
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.deleteCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "brand"],
      });
    },
  });
};

// 운송장번호 등록
export const useUpdateTrackingNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.updateTrackingNumber(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "brand"],
      });
    },
  });
};

// 엑셀 일괄 운송장 업로드
export const useUploadShipmentExcel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.uploadShipmentExcel(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "brand"],
      });
    },
  });
};

// 캠페인 생성
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.createCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "brand"],
      });
    },
  });
};

// 캠페인에 인플루언서 초대
export const useInviteInfluencerToCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.inviteInfluencerToCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "invited", "brand"],
      });
    },
  });
};

// AgencyCampaignManagement
export const useCampaignListByAgency = ({ page, status, enabled = true }) => {
  return useQuery({
    queryKey: ["campaign", "list", "agency", page, status],
    queryFn: () => Router.Api.getCampaignsByAgency({ page, status }),
    staleTime: 1000 * 60,
    enabled,
  });
};

export const useAppliedInfluencersByAgency = ({
  campaignId,
  status,
  enabled = true,
}) => {
  return useQuery({
    queryKey: ["campaign", "applied", "agency", campaignId, status],
    queryFn: () =>
      Router.Api.getAppliedInfluencersByAgency({ campaignId, status }),
    enabled,
  });
};

export const useCancelCampaignByAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.cancelCampaignByAgency(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "agency"],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "agency"],
      });
    },
  });
};

// MyCampaign
// 크리에이터 본인의 캠페인 리스트 조회
export const useCampaignListByInfluencer = ({ page, status }) => {
  return useQuery({
    queryKey: ["campaign", "list", "influencer", page, status],
    queryFn: () => Router.Api.getCampaignsByInfluencer({ page, status }),
  });
};

// 캠페인 신청 취소
export const useCancelCampaignByInfluencer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.cancelCampaign(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "influencer"],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", "influencer"],
      });
    },
  });
};

// 캠페인 초대 거절 (크리에이터 목록에서 사라지므로 list 무효화)
export const useRejectCampaignInvitationByInfluencer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId) => Router.Api.rejectCampaignInvitation(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "list", "influencer"],
      });
    },
  });
};

// 크리에이터 미션 사전 제출 (포스팅)
export const useUploadPreMissionByInfluencer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.uploadPreMission(variables),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", variables.campaign_id],
      });
    },
  });
};

// 크리에이터 미션 제출
export const useUploadMissionByInfluencer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.uploadMission(variables),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied", variables.campaign_id],
      });
    },
  });
};

// 크리에이터 배송 정보 수정
export const useUpdateShipmentByInfluencer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => Router.Api.updateShipment(variables),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign", "applied"],
      });
    },
  });
};

// 크리에이터에게 캠페인 추천
export const useRecommendCampaignToInfluencer = (enabled = false) => {
  return useQuery({
    queryKey: ["campaign", "recommended", "influencer"],
    queryFn: () => Router.Api.getRecommendedCampaigns(),
    enabled,
    staleTime: 1000 * 60 * 10,
  });
};
